/**
 * Interoperability Transaction Monitoring — mock service
 *
 * Provides mock ADT transaction records with pipeline-stage progress and
 * retry/replay behavior. Payloads are intentionally sanitized mock strings
 * — never real HL7/PHI — and are never logged to the console.
 */
import type { PipelineStage, TransactionFilters, TransactionRecord } from '../types';
import { mockDelay } from './mock.util';

function stages(...states: PipelineStage['state'][]): PipelineStage[] {
  const keys: PipelineStage['key'][] = ['RECEIVED', 'PARSED', 'MAPPED', 'API'];
  return keys.map((key, i) => ({ key, state: states[i] ?? 'PENDING' }));
}

let transactions: TransactionRecord[] = [
  {
    id: 'txn-1',
    receivedAt: '2026-08-22T10:15:03.000Z',
    messageControlId: 'MSG00231',
    client: 'Mercy General',
    messageType: 'ADT^A01',
    pipeline: stages('DONE', 'DONE', 'DONE', 'DONE'),
    status: 'SUCCESS',
    apiResponse: '200',
    retryCount: 0,
    maxRetries: 3,
    errorDetail: null,
    rawMessage: 'MSH|^~\\&|EPIC|MERCY-GEN|BRIDGELINK|TMS|20260822101503||ADT^A01|MSG00231|P|2.5.1',
    parsedCanonicalJson: '{ "patient": { "mrn": "MOCK-0001" }, "encounter": { "visitNumber": "V-1001" } }',
    apiRequestSummary: 'POST /v1/patients/admit',
    apiResponseSummary: '200 OK',
    retryHistory: [],
  },
  {
    id: 'txn-2',
    receivedAt: '2026-08-22T10:14:47.000Z',
    messageControlId: 'MSG00230',
    client: 'Mercy General',
    messageType: 'ADT^A08',
    pipeline: stages('DONE', 'DONE', 'FAILED', 'PENDING'),
    status: 'FAILED',
    apiResponse: '—',
    retryCount: 0,
    maxRetries: 3,
    errorDetail: 'Field mapping failed: PID-8 value "9" has no crosswalk entry and no fallback default is configured.',
    rawMessage: 'MSH|^~\\&|EPIC|MERCY-GEN|BRIDGELINK|TMS|20260822101447||ADT^A08|MSG00230|P|2.5.1',
    parsedCanonicalJson: '{ "patient": { "mrn": "MOCK-0002" } }',
    apiRequestSummary: '—',
    apiResponseSummary: '—',
    retryHistory: [],
  },
  {
    id: 'txn-3',
    receivedAt: '2026-08-22T10:14:02.000Z',
    messageControlId: 'MSG00229',
    client: "St. Luke's",
    messageType: 'ADT^A04',
    pipeline: stages('DONE', 'DONE', 'DONE', 'FAILED'),
    status: 'FAILED',
    apiResponse: '504',
    retryCount: 0,
    maxRetries: 3,
    errorDetail: 'API 504 Gateway Timeout at 10:14:09 — downstream Patient-Encounter service did not respond within 8000ms (attempt 1 of 3).',
    rawMessage: 'MSH|^~\\&|EPIC|ST-LUKES|BRIDGELINK|TMS|20260822101402||ADT^A04|MSG00229|P|2.5.1',
    parsedCanonicalJson: '{ "patient": { "mrn": "MOCK-0003" }, "encounter": { "visitNumber": "V-2004" } }',
    apiRequestSummary: 'POST /v1/patients/register',
    apiResponseSummary: '504 Gateway Timeout',
    retryHistory: [
      { attempt: 1, timestamp: '2026-08-22T10:14:09.000Z', result: '504 timeout' },
    ],
  },
  {
    id: 'txn-4',
    receivedAt: '2026-08-22T10:13:55.000Z',
    messageControlId: 'MSG00228',
    client: "St. Luke's",
    messageType: 'ADT^A03',
    pipeline: stages('DONE', 'DONE', 'DONE', 'FAILED'),
    status: 'RETRYING',
    apiResponse: 'timeout',
    retryCount: 2,
    maxRetries: 5,
    errorDetail: 'Timed out waiting for downstream discharge acknowledgement.',
    rawMessage: 'MSH|^~\\&|EPIC|ST-LUKES|BRIDGELINK|TMS|20260822101355||ADT^A03|MSG00228|P|2.5.1',
    parsedCanonicalJson: '{ "patient": { "mrn": "MOCK-0004" } }',
    apiRequestSummary: 'POST /v1/patients/discharge',
    apiResponseSummary: 'timeout',
    retryHistory: [
      { attempt: 1, timestamp: '2026-08-22T10:14:00.000Z', result: 'timeout' },
      { attempt: 2, timestamp: '2026-08-22T10:19:00.000Z', result: 'timeout' },
    ],
  },
  {
    id: 'txn-5',
    receivedAt: '2026-08-22T10:13:40.000Z',
    messageControlId: 'MSG00227',
    client: 'Mercy General',
    messageType: 'ADT^A01',
    pipeline: stages('DONE', 'PENDING', 'PENDING', 'PENDING'),
    status: 'QUEUED',
    apiResponse: '—',
    retryCount: 0,
    maxRetries: 3,
    errorDetail: null,
    rawMessage: 'MSH|^~\\&|EPIC|MERCY-GEN|BRIDGELINK|TMS|20260822101340||ADT^A01|MSG00227|P|2.5.1',
    parsedCanonicalJson: '—',
    apiRequestSummary: '—',
    apiResponseSummary: '—',
    retryHistory: [],
  },
];

export async function listTransactions(filters?: Partial<TransactionFilters>): Promise<TransactionRecord[]> {
  await mockDelay();
  let result = transactions.map((t) => ({ ...t }));
  if (filters?.client) result = result.filter((t) => t.client === filters.client);
  if (filters?.status) result = result.filter((t) => t.status === filters.status);
  if (filters?.messageType) result = result.filter((t) => t.messageType === filters.messageType);
  if (filters?.search) {
    const q = filters.search.trim().toLowerCase();
    result = result.filter(
      (t) => t.messageControlId.toLowerCase().includes(q) || t.client.toLowerCase().includes(q),
    );
  }
  return result;
}

export async function replayTransaction(id: string): Promise<TransactionRecord> {
  await mockDelay();
  const existing = transactions.find((t) => t.id === id);
  if (!existing) throw new Error('Transaction not found');
  const updated: TransactionRecord = {
    ...existing,
    status: 'SUCCESS',
    apiResponse: '200',
    pipeline: stages('DONE', 'DONE', 'DONE', 'DONE'),
    errorDetail: null,
    apiResponseSummary: '200 OK',
  };
  transactions = transactions.map((t) => (t.id === id ? updated : t));
  return { ...updated };
}

export async function editAndReplayTransaction(id: string, sanitizedPayload: string): Promise<TransactionRecord> {
  await mockDelay();
  const existing = transactions.find((t) => t.id === id);
  if (!existing) throw new Error('Transaction not found');
  const updated: TransactionRecord = {
    ...existing,
    rawMessage: sanitizedPayload,
    status: 'SUCCESS',
    apiResponse: '200',
    pipeline: stages('DONE', 'DONE', 'DONE', 'DONE'),
    errorDetail: null,
    apiResponseSummary: '200 OK',
  };
  transactions = transactions.map((t) => (t.id === id ? updated : t));
  return { ...updated };
}

export async function cancelRetry(id: string): Promise<TransactionRecord> {
  await mockDelay();
  const existing = transactions.find((t) => t.id === id);
  if (!existing) throw new Error('Transaction not found');
  const updated: TransactionRecord = {
    ...existing,
    status: 'FAILED',
    apiResponseSummary: 'Retry cancelled by user',
  };
  transactions = transactions.map((t) => (t.id === id ? updated : t));
  return { ...updated };
}

export function getKnownClients(): string[] {
  return Array.from(new Set(transactions.map((t) => t.client)));
}

export function getKnownMessageTypes(): string[] {
  return Array.from(new Set(transactions.map((t) => t.messageType)));
}
